import React from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Link from 'next/link';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useTheme } from 'styled-components';
import UrlMapper from '@/config/url-mapper';
import { useSignup } from '@/services/auth';
import { Asterisk, Button, Checkbox, Textfield, Toast, Typography } from '@/components/atoms';
import { UnexpectedError } from '@/components/molecules';
import { ButtonContainer, CheckboxContainer, CustomForm, TextWrapper } from './SignupForm.styles';

type SignupFormType = {
    firstName: string;
    lastName: string;
    email: string;
    n_password: string;
    isSubscribed: boolean;
    acceptTerms: boolean;
};

const SignupForm: React.FC = () => {
    const { t } = useTranslation(); // Translations
    const router = useRouter(); // Router
    const { executeRecaptcha } = useGoogleReCaptcha(); // Recaptcha
    const theme = useTheme(); // Get current theme
    const [globalError, setGlobalError] = React.useState<boolean>(false);
    const { signup, loading } = useSignup();
    const [showError, setShowError] = React.useState(false);

    // Forms initial values
    const initialValuesSignUp = {
        firstName: '',
        lastName: '',
        email: '',
        n_password: '',
        isSubscribed: false,
        acceptTerms: false,
    };

    // Validation schema
    const validationSchemaSignUp = Yup.object({
        firstName: Yup.string().required(t('auth:signup-form.required.name')),
        lastName: Yup.string().required(t('auth:signup-form.required.lastname')),
        email: Yup.string().email(t('auth:signup-form.invalid.email')).required(t('auth:signup-form.required.email')),
        n_password: Yup.string()
            .required(t('auth:signup-form.required.password'))
            .min(8, 'Password must have at least 8 characters')
            .matches(/[0-9]/, t('auth:signup-form.invalid-numeric.password'))
            .matches(/[a-z]/, t('auth:signup-form.invalid-lowercase.password'))
            .matches(/[A-Z]/, t('auth:signup-form.invalid-uppercase.password'))
            .matches(/[.,*!?¿¡/#$@%&]/, t('auth:signup-form.invalid-symbol.password'))
            .matches(/^(\S*)$/, t('auth:signup-form.invalid-space.password')),
        acceptTerms: Yup.boolean().test('validation-check', '', (value) => {
            if (!value) {
                setShowError(true);
            }
            return true;
        }),
    });

    const handleOnSubmit = async (
        values: SignupFormType,
        {
            setFieldError,
        }: {
            setFieldError: (field: string, message: string | undefined) => void;
        }
    ) => {
        setGlobalError(false);

        if (executeRecaptcha) {
            const token = await executeRecaptcha('SIGNUP');

            await signup({
                variables: {
                    firstName: values.firstName,
                    lastName: values.lastName,
                    email: values.email,
                    password: values.n_password,
                    subscribeNewsletter: values.isSubscribed,
                    acceptTerms: values.acceptTerms,
                    recaptchaToken: token,
                    callbackUrl: UrlMapper.activate_account_callback,
                },
                onCompleted: async (data) => {
                    if (data?.signup?.status === 200) {
                        await router.push({
                            pathname: UrlMapper.signup_verification,
                        });
                    } else {
                        if (data?.signup?.errors && data?.signup?.errors?.length > 0) {
                            data?.signup?.errors?.map((err) => {
                                const field = err?.field || '';
                                const message = err?.messages.join(',') || '';
                                if (!field) {
                                    setGlobalError(true);
                                } else {
                                    setFieldError(field, message);
                                }
                            });
                        } else {
                            setGlobalError(true);
                        }
                    }
                },
                onError: async () => {
                    setGlobalError(true);
                },
            });
        }
    };

    return (
        <>
            <Typography as="h1" variant="h6" align="center">
                {t('auth:signup-form.section.title')}
            </Typography>

            <TextWrapper>
                {globalError && <UnexpectedError />}

                {!globalError && (
                    <Typography variant="body2" align="center">
                        {t('auth:signup-form.section.description')}
                    </Typography>
                )}
            </TextWrapper>

            <Toast variant="error" showToast={showError} setShowToast={setShowError} top="25px">
                <Typography variant="body2" color={theme.colors.error.error50}>
                    {t('auth:signup-form.invalid.terms')}
                </Typography>
            </Toast>

            <Formik enableReinitialize initialValues={initialValuesSignUp} validationSchema={validationSchemaSignUp} onSubmit={handleOnSubmit}>
                {({ errors, values, touched, handleChange, handleBlur, setFieldValue }) => {
                    return (
                        <CustomForm>
                            <Textfield
                                id="firstName"
                                type="text"
                                label={<Asterisk text={t('auth:signup-form.label.name')} />}
                                placeholder={t('auth:signup-form.placeholder.name')}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values?.firstName}
                                errors={touched.firstName && errors.firstName}
                                success={(touched.firstName || values?.firstName !== '') && !errors.firstName}
                            />
                            <Textfield
                                id="lastName"
                                type="text"
                                label={<Asterisk text={t('auth:signup-form.label.lastname')} />}
                                placeholder={t('auth:signup-form.placeholder.lastname')}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values?.lastName}
                                errors={touched.lastName && errors.lastName}
                                success={(touched.lastName || values?.lastName !== '') && !errors.lastName}
                            />
                            <Textfield
                                id="email"
                                type="text"
                                label={<Asterisk text={t('auth:signup-form.label.email')} />}
                                placeholder={t('auth:signup-form.placeholder.email')}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values?.email}
                                errors={touched.email && errors.email}
                                success={(touched.email || values?.email !== '') && !errors.email}
                            />
                            <Textfield
                                id="n_password"
                                type="password"
                                revealPasswordIcon
                                label={<Asterisk text={t('auth:signup-form.label.password')} />}
                                placeholder={t('auth:signup-form.placeholder.password')}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values?.n_password}
                                errors={touched.n_password && errors.n_password}
                                success={(touched.n_password || values?.n_password !== '') && !errors.n_password}
                                autoComplete="new-password"
                            />
                            <CheckboxContainer>
                                <Checkbox
                                    label={
                                        <>
                                            {t('auth:signup-form.label.terms-accept')}
                                            <Link href={UrlMapper.terms_and_conditions}>{` ${t('auth:signup-form.label.terms-conditions')} `}</Link>
                                            {`${t('auth:signup-form.label.terms-and')} `}
                                            <Link href={UrlMapper.privacy_policy}>
                                                <Asterisk text={t('auth:signup-form.label.privacy-policy')} />
                                            </Link>
                                        </>
                                    }
                                    checked={values.acceptTerms}
                                    height="16px"
                                    width="16px"
                                    onChange={(e) => {
                                        void setFieldValue('acceptTerms', e.target.checked);
                                        if (e.target.checked) setShowError(false);
                                    }}
                                />
                                <Checkbox
                                    label={t('auth:signup-form.label.subscribed')}
                                    checked={values.isSubscribed}
                                    height="16px"
                                    width="16px"
                                    onChange={(e) => void setFieldValue('isSubscribed', e.target.checked)}
                                />
                            </CheckboxContainer>
                            <ButtonContainer>
                                <Button content={t('auth:button.login')} type="button" disabled={loading} variant="outlined" onClick={() => void router.push(UrlMapper.login)} />
                                <Button content={t('auth:button.signup')} type="submit" variant="filled" isSubmitting={loading} disabled={loading} />
                            </ButtonContainer>
                        </CustomForm>
                    );
                }}
            </Formik>
        </>
    );
};

export default SignupForm;
